import React from "react";
import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useScheduleEvents } from "../../hooks/useScheduleEvents";
import { IconApp } from "../IconApp";
import { weekDaysES } from "../Options/OptionsWeekDays";
import { Link } from "react-router-dom";
import { Empty } from "antd";
import { ScheduleTableByWeekDayCell } from "./ScheduleTableByWeekDayCell ";

export const ScheduleTableByWeek = ({
    name,
    schedules = [],
    session,
    academic_group,
    showTeacher = true,
    showAcademicGroup = true,
    compact = false,
}) => {
    const { calendars } = useSelector((state) => state.calendarSchedule);
    const { tiny_name } = useSelector((state) => state.config);
    const { events } = useScheduleEvents(name, calendars);

    const EAD = tiny_name === "EAD";
    const days = [
        "Lunes",
        "Martes",
        "Miércoles",
        "Jueves",
        "Viernes",
        "Sábado",
    ];

    //* Agrupar horarios repetidos
    const groupSchedules = () => {
        const groupedSchedules = {};

        schedules?.forEach((schedule) => {
            const uniqueKey = `${schedule?.class?.id}_${
                schedule?.teacher?.id
            }_${schedule?.identifier || 0}`;

            if (!groupedSchedules[uniqueKey]) {
                groupedSchedules[uniqueKey] = {
                    original: [schedule],
                    duplicated: [],
                };
            } else {
                groupedSchedules[uniqueKey].duplicated.push(schedule);
            }
        });

        const groups = Object.values(groupedSchedules);

        if (EAD) {
            const moduleOrder = {
                M1: 1,
                M2: 2,
                M3: 3,
                LG: 4,
                LG_TESIS: 5,
            };

            groups.sort((a, b) => {
                const modA = a.original[0]?.module?.toUpperCase() || "";
                const modB = b.original[0]?.module?.toUpperCase() || "";
                return (moduleOrder[modA] || 99) - (moduleOrder[modB] || 99);
            });
        }

        return groups;
    };

    //* Determine which days have schedules
    const dayMap = {
        Monday: 0,
        Tuesday: 1,
        Wednesday: 2,
        Thursday: 3,
        Friday: 4,
        Saturday: 5,
    };

    const hasSchedules = Array(6).fill(false);
    schedules?.forEach((schedule) => {
        const dayIndex = dayMap[schedule?.day];
        if (dayIndex !== undefined) {
            hasSchedules[dayIndex] = true;
        }
    });

    //* Filter days to show only those with schedules when compact is true
    const filteredDays = compact
        ? days.filter((_, index) => hasSchedules[index])
        : days;

    return (
        <>
            {schedules?.length > 0 ? (
                <div className="table-responsive">
                    <Table
                        responsive="xl"
                        size={compact ? "sm" : "xl"}
                        bordered
                        striped
                        hover
                        className="mt-2"
                    >
                        <thead>
                            <tr
                                className={`align-middle text-center ${
                                    compact ? "text-size-12" : "text-size-13"
                                } bg-blue`}
                            >
                                <th>
                                    <IconApp iconClassName="fas fa-book-reader" />
                                    <span>Materia</span>
                                </th>
                                {showTeacher && (
                                    <th>
                                        <IconApp iconClassName="fas fa-chalkboard-teacher" />
                                        <span>Docente</span>
                                    </th>
                                )}
                                {showAcademicGroup && (
                                    <th>
                                        <IconApp iconClassName="fas fa-users" />
                                        <span>Grupo</span>
                                    </th>
                                )}
                                {EAD ? (
                                    <th>
                                        <IconApp iconClassName="fas fa-puzzle" />
                                        <span>Módulo</span>
                                    </th>
                                ) : (
                                    <>
                                        {filteredDays.map((day) => (
                                            <th key={day}>{day}</th>
                                        ))}
                                    </>
                                )}
                            </tr>
                        </thead>

                        <tbody>
                            {groupSchedules()?.map(
                                (schedule, scheduleIndex) => {
                                    const dayColumns = Array(6)
                                        .fill(null)
                                        .map(() => []);

                                    // Procesar horarios originales y duplicados
                                    const allSchedules = [
                                        ...schedule?.original ?? [],
                                        ...schedule?.duplicated  ?? [],
                                    ];

                                    allSchedules.forEach((allSchedule) => {
                                        const dayIndex =
                                            dayMap[allSchedule?.day];

                                        if (dayIndex !== undefined) {
                                            dayColumns[dayIndex].push({
                                                id: allSchedule?.id,
                                                schedule: allSchedule,
                                                start: allSchedule?.start,
                                                end: allSchedule?.end,
                                            });
                                        }
                                    });

                                    return (
                                        <tr
                                            key={scheduleIndex}
                                            className={
                                                compact
                                                    ? "text-size-10"
                                                    : "text-size-11"
                                            }
                                            data-subject_id={
                                                schedule?.original[0]?.class.id
                                            }
                                            data-academic_schedule_id={
                                                schedule?.original[0]?.id
                                            }
                                            data-cycle_id={
                                                schedule?.original[0]?.cycle_id
                                            }
                                        >
                                            <td
                                                className={`align-middle ${
                                                    !compact &&
                                                    "font-weight-bold"
                                                }`}
                                            >
                                                {(() => {
                                                    const original =
                                                        schedule?.original?.[0];
                                                    if (!original) return "";

                                                    if (compact)
                                                        return (
                                                            original.class
                                                                ?.name ?? ""
                                                        );

                                                    const fullName =
                                                        original.class
                                                            ?.full_name ?? "";
                                                    const sublevel =
                                                        original.english_sublevel
                                                            ? ` - ${original.english_sublevel}`
                                                            : "";

                                                    return fullName + sublevel;
                                                })()}
                                            </td>
                                            {showTeacher && (
                                                <td
                                                    data-th="Docente:"
                                                    data-center
                                                    className="align-middle"
                                                >
                                                    {
                                                        schedule?.original[0]
                                                            ?.teacher?.username
                                                    }
                                                </td>
                                            )}
                                            {showAcademicGroup && (
                                                <td
                                                    data-th="Grupo:"
                                                    data-center
                                                    className="align-middle text-center no-wrap"
                                                >
                                                    {schedule?.original[0]
                                                        ?.category !==
                                                    "normal" ? (
                                                        <>
                                                            {schedule
                                                                ?.original[0]
                                                                ?.category ===
                                                            "diplomado" ? (
                                                                <span>
                                                                    Diplomado
                                                                </span>
                                                            ) : (
                                                                <span>-</span>
                                                            )}
                                                        </>
                                                    ) : ["student", "teacher"].includes(session) ? (
                                                        <span>
                                                            {
                                                                schedule
                                                                    ?.original[0]
                                                                    ?.group
                                                                    ?.name
                                                            }
                                                        </span>
                                                    ) : (
                                                        <OverlayTrigger
                                                            placement={"bottom"}
                                                            overlay={
                                                                <Tooltip
                                                                    hidden={
                                                                        false
                                                                    }
                                                                >
                                                                    {
                                                                        schedule
                                                                            ?.original[0]
                                                                            ?.group
                                                                            ?.code
                                                                    }
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <Link
                                                                className="text-info font-weight-bold"
                                                                to={`/app/operators/academicgroups/${schedule?.original[0]?.group?.id}`}
                                                                target={
                                                                    "_blank"
                                                                }
                                                                rel="noopener noreferrer"
                                                            >
                                                                {
                                                                    schedule
                                                                        ?.original[0]
                                                                        ?.group
                                                                        ?.name
                                                                }
                                                            </Link>
                                                        </OverlayTrigger>
                                                    )}
                                                </td>
                                            )}

                                            {(EAD
                                                ? dayColumns.slice(0, 1)
                                                : dayColumns.filter(
                                                      (_, index) =>
                                                          compact
                                                              ? hasSchedules[
                                                                    index
                                                                ]
                                                              : true
                                                  )
                                            )?.map((daySchedules, dayIndex) => (
                                                <td
                                                    data-th={
                                                        weekDaysES[
                                                            filteredDays.indexOf(
                                                                days[dayIndex]
                                                            ) >= 0
                                                                ? filteredDays.indexOf(
                                                                      days[
                                                                          dayIndex
                                                                      ]
                                                                  )
                                                                : dayIndex
                                                        ].label
                                                    }
                                                    data-center
                                                    key={dayIndex}
                                                    className="align-middle text-center py-1"
                                                >
                                                    <div className="d-flex flex-column gap-1">
                                                        {daySchedules?.map(
                                                            (time) => (
                                                                <ScheduleTableByWeekDayCell
                                                                    key={
                                                                        time?.id
                                                                    }
                                                                    time={time}
                                                                    session={
                                                                        session
                                                                    }
                                                                    events={
                                                                        events
                                                                    }
                                                                    academic_group={
                                                                        academic_group
                                                                    }
                                                                    name={name}
                                                                    asPeriod={
                                                                        EAD
                                                                    }
                                                                    compact={
                                                                        compact
                                                                    }
                                                                />
                                                            )
                                                        )}
                                                    </div>
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                }
                            )}
                        </tbody>
                    </Table>
                </div>
            ) : (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No se encontraron horarios."
                />
            )}
        </>
    );
};
